import React from "react";
import CountWidget from "../Widgets/CountWidget";
import RateWidget from "../Widgets/RateWidget";
import CallOutcomesWidget from "../Widgets/CallOutcomesWidget";
import LinkedInOverviewWidget from "../Widgets/LinkedInOverviewWidget";
import CallsByMemberWidget from "../Widgets/CallsByMemberWidget";
import LatestRepliesWidget from "../Widgets/LatestRepliesWidget";
import { RATE_WIDGET_IDS, WIDGET_META } from "../utils/widgetConstants";

const PREVIEW_TREND_UP = { percent: 12, direction: "up" };
const PREVIEW_TREND_DOWN = { percent: 8, direction: "down" };

const PREVIEW_RATE_CHART = [
  { label: "Mon", value: 28 },
  { label: "Tue", value: 34 },
  { label: "Wed", value: 22 },
  { label: "Thu", value: 41 },
  { label: "Fri", value: 36 },
  { label: "Sat", value: 19 },
  { label: "Sun", value: 30 }
];

const PREVIEW_CALL_OUTCOMES = [
  { outcome: "Connected", count: 28 },
  { outcome: "No Answer", count: 47 },
  { outcome: "Voicemail", count: 15 },
  { outcome: "Not Logged", count: 9 }
];

const PREVIEW_LINKEDIN_DATA = {
  connectionsSent: 50,
  connectionsAccepted: 20,
  messagesSent: 30,
  messagesReplied: 8,
  profileViewed: 60
};


const PREVIEW_SERIES = [12, 18, 9, 24, 30, 15, 21].map((count, idx) => {
  const date = new Date();
  date.setDate(date.getDate() - (6 - idx));
  return { date: date.toISOString(), count };
});


const PREVIEW_LATEST_REPLIES = [
  {
    task: { id: "p1", taskType: "EMAIL", touchPoint: { subject: "Re: Quick question about pricing" }, replyDate: new Date(Date.now() - 1 * 3600 * 1000).toISOString() },
    contact: [{ email: "alice@acme.com" }]
  },
  {
    task: { id: "p2", taskType: "LINKEDIN", touchPoint: { subject: "Thanks for reaching out, let's connect" }, replyDate: new Date(Date.now() - 4 * 3600 * 1000).toISOString() },
    contact: [{ email: "bob@brightline.io" }]
  },
  {
    task: { id: "p3", taskType: "EMAIL", touchPoint: { subject: "Re: Demo follow-up" }, replyDate: new Date(Date.now() - 26 * 3600 * 1000).toISOString() },
    contact: [{ email: "carol@northwind.com" }]
  },
  {
    task: { id: "p4", taskType: "LINKEDIN", touchPoint: { subject: "Interested, send more details" }, replyDate: new Date(Date.now() - 30 * 3600 * 1000).toISOString() },
    contact: [{ email: "david@globex.com" }]
  },
  {
    task: { id: "p5", taskType: "EMAIL", touchPoint: { subject: "Re: Proposal review" }, replyDate: new Date(Date.now() - 50 * 3600 * 1000).toISOString() },
    contact: [{ email: "erin@initech.com" }]
  }
];

const PREVIEW_CALLS_BY_MEMBER = [
  { name: "Alice", callsMade: 18 },
  { name: "Bob", callsMade: 12 },
  { name: "Carol", callsMade: 25 },
  { name: "David", callsMade: 9 }
];

const PREVIEW_DATA_BY_WIDGET = {
  EMAILS_SENT: { value: 1284, trend: PREVIEW_TREND_UP },
  BOUNCES: { value: 24, trend: PREVIEW_TREND_DOWN },
  MANUAL_EMAILS_SENT: { value: 142, trend: PREVIEW_TREND_UP },
  SEQUENCE_EMAILS_SENT: { value: 1142, trend: PREVIEW_TREND_UP },
  CALLS_CONNECTED: { value: 86, trend: PREVIEW_TREND_UP },
  TOTAL_CALLING_MINUTES: { value: 312, trend: PREVIEW_TREND_UP },
  TOTAL_CALLS_MADE: { value: 124, trend: PREVIEW_TREND_UP },
  AVG_CALL_DURATION: { value: "3:04", trend: PREVIEW_TREND_DOWN },
  CONNECTIONS_SENT: { value: 50, trend: PREVIEW_TREND_UP },
  CONNECTIONS_ACCEPTED: { value: 20, trend: PREVIEW_TREND_UP },
  TOTAL_LINKEDIN_MESSAGES_SENT: { value: 30, trend: PREVIEW_TREND_UP },
  LINKEDIN_REPLIES_RECEIVED: { value: 8, trend: PREVIEW_TREND_UP },
  TASKS_DUE: { value: 18, trend: PREVIEW_TREND_DOWN },
  TASKS_COMPLETED: { value: 45, trend: PREVIEW_TREND_UP },
  MEETINGS_BOOKED: { value: 7, trend: PREVIEW_TREND_UP },
  CONTACTS_ADDED_TO_SEQUENCE: { value: 96, trend: PREVIEW_TREND_UP }
};

const PREVIEW_RATE_BY_WIDGET = {
  EMAIL_OPEN_RATE: { currentRate: 40.3, trend: PREVIEW_TREND_UP, chartData: PREVIEW_RATE_CHART },
  EMAIL_REPLY_RATE: { currentRate: 12.7, trend: PREVIEW_TREND_UP, chartData: PREVIEW_RATE_CHART },
  CONNECT_RATE: { currentRate: 18.5, trend: PREVIEW_TREND_DOWN, chartData: PREVIEW_RATE_CHART },
  LINKEDIN_REPLY_RATE: { currentRate: 22.1, trend: PREVIEW_TREND_UP, chartData: PREVIEW_RATE_CHART }
};

const WidgetPreview = ({ widgetId }) => {
  if (!widgetId) return null;

  if (RATE_WIDGET_IDS.has(widgetId)) {
    const d = PREVIEW_RATE_BY_WIDGET[widgetId] || {};
    return (
      <RateWidget
        currentRate={d.currentRate}
        trend={d.trend}
        chartData={d.chartData}
        dateFilter="7d"
      />
    );
  }

  if (widgetId === "CALL_OUTCOMES") {
    return <CallOutcomesWidget callOutcomes={PREVIEW_CALL_OUTCOMES} />;
  }

  if (widgetId === "LINKEDIN_OVERVIEW") {
    return <LinkedInOverviewWidget data={PREVIEW_LINKEDIN_DATA} />;
  }

  if (widgetId === "CALLS_BY_MEMBER") {
    return <CallsByMemberWidget callsByMember={PREVIEW_CALLS_BY_MEMBER} />;
  }

  if (widgetId === "LATEST_REPLIES") {
    return <LatestRepliesWidget replies={PREVIEW_LATEST_REPLIES} />;
  }

  const d = PREVIEW_DATA_BY_WIDGET[widgetId] || { value: 0, trend: null };
  const hasChart = !!WIDGET_META[widgetId]?.seriesKey;
  return (
    <CountWidget
      value={d.value}
      trend={d.trend}
      series={hasChart ? PREVIEW_SERIES : null}
      dateFilter="7d"
    />
  );
};

export default WidgetPreview;
